// ** React Import
import { forwardRef, useState } from "react";

// ** MUI Imports
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";

const TextFieldStyled = styled(TextField)(
  ({
    theme,
    customBorderColor,
    borderoutlinedColor,
    customTextColor,
    customFontSize,
  }) => ({
    alignItems: "flex-start",
    "& .MuiInputLabel-root": {
      transform: "none",
      lineHeight: 1.154,
      position: "relative",
      marginBottom: theme.spacing(1),
      // fontSize: theme.typography.body2.fontSize,
      fontSize: customFontSize,
      // color: `${theme.palette.text.primary} !important`
      color: `${customTextColor} !important`,
    },
    "& .MuiInputBase-root": {
      borderRadius: 8,
      backgroundColor: "transparent !important",
      // border: `1px solid rgba(${theme.palette.customColors?.main}, 0.2)`,
      border: `1px solid ${customBorderColor}`,
      transition: theme.transitions.create(["border-color"], {
        duration: theme.transitions.duration.shorter,
      }),
      "&:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error):hover": {
        borderColor: `${customBorderColor}`,
      },
      "&:before, &:after": {
        display: "none",
      },
      "&.MuiInputBase-sizeSmall": {
        borderRadius: 6,
      },
      "&.Mui-error": {
        borderColor: theme.palette.error?.main,
      },
      "&.Mui-focused": {
        boxShadow: theme.shadows[0],
        "& .MuiInputBase-input:not(.MuiInputBase-readOnly):not([readonly])::placeholder":
          {
            transform: "translateX(4px)",
          },
        "&.MuiInputBase-colorPrimary": {
          borderColor: `${borderoutlinedColor}`,
        },
        "&.MuiInputBase-colorSecondary": {
          borderColor: theme.palette.secondary?.main,
        },
        "&.MuiInputBase-colorInfo": {
          borderColor: theme.palette.info?.main,
        },
        "&.MuiInputBase-colorSuccess": {
          borderColor: theme.palette.success?.main,
        },
        "&.MuiInputBase-colorWarning": {
          borderColor: theme.palette.warning?.main,
        },
        "&.MuiInputBase-colorError": {
          borderColor: theme.palette.error?.main,
        },
        "&.Mui-error": {
          borderColor: theme.palette.error?.main,
        },
      },
      "&.Mui-disabled": {
        backgroundColor: `${theme.palette.action.selected} !important`,
      },
      "& .MuiInputAdornment-root": {
        marginTop: "0 !important",
      },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.secondary,
      "&:not(textarea)": {
        padding: "15.5px 13px",
      },
      "&:not(textarea).MuiInputBase-inputSizeSmall": {
        padding: "7.5px 13px",
      },
      "&:not(.MuiInputBase-readOnly):not([readonly])::placeholder": {
        transition: theme.transitions.create(["opacity", "transform"], {
          duration: theme.transitions.duration.shorter,
        }),
      },

      // ** For Autocomplete
      "&.MuiInputBase-inputAdornedStart:not(.MuiAutocomplete-input)": {
        paddingLeft: 0,
      },
      "&.MuiInputBase-inputAdornedEnd:not(.MuiAutocomplete-input)": {
        paddingRight: 0,
      },
    },
    "& .MuiFormHelperText-root": {
      lineHeight: 1.154,
      margin: theme.spacing(1, 0, 0),
      color: theme.palette.text.secondary,
      fontSize: theme.typography.body2.fontSize,
      "&.Mui-error": {
        color: theme.palette.error?.main,
      },
    },

    // ** For Select
    "& .MuiSelect-select:focus, & .MuiNativeSelect-select:focus": {
      backgroundColor: "transparent",
    },
    "& .MuiSelect-filled .MuiChip-root": {
      height: 22,
    },

    // ** For Autocomplete
    "& .MuiAutocomplete-input": {
      paddingLeft: "6px !important",
      paddingTop: "7.5px !important",
      paddingBottom: "7.5px !important",
      "&.MuiInputBase-inputSizeSmall": {
        paddingLeft: "6px !important",
        paddingTop: "2.5px !important",
        paddingBottom: "2.5px !important",
      },
    },
    "& .MuiAutocomplete-inputRoot": {
      paddingTop: "8px !important",
      paddingLeft: "8px !important",
      paddingBottom: "8px !important",
      "&:not(.MuiInputBase-sizeSmall).MuiInputBase-adornedStart": {
        paddingLeft: "13px !important",
      },
      "&.MuiInputBase-sizeSmall": {
        paddingTop: "5px !important",
        paddingLeft: "5px !important",
        paddingBottom: "5px !important",
        "& .MuiAutocomplete-tag": {
          margin: 2,
          height: 22,
        },
      },
    },

    // ** For Textarea
    "& .MuiInputBase-multiline": {
      padding: "15.25px 13px",
      "&.MuiInputBase-sizeSmall": {
        padding: "7.25px 13px",
      },
      "& textarea.MuiInputBase-inputSizeSmall:placeholder-shown": {
        overflowX: "hidden",
      },
    },

    // ** For Date Picker
    "& + .react-datepicker__close-icon": {
      top: 11,
      "&:after": {
        fontSize: "1.6rem !important",
      },
    },
  })
);

const CustomTextField = forwardRef((props, ref) => {

  const [isAnimating, setIsAnimating] = useState(false); // State for managing animation

  // Function to trigger the animation on focus
  const handleFocus = () => {
    setIsAnimating(true);
  };

  // Function to reset the animation after it finishes
  const handleBlur = () => {
    setIsAnimating(false);
  };


  // ** Props
  const { size = "small", className, InputLabelProps, ...rest } = props;
  return (
    <TextFieldStyled
      customBorderColor={props?.customBorderColor ?? "#e8e8e8"}
      borderoutlinedColor={props?.borderoutlinedColor ?? "#685dd8"}
      customTextColor={props?.customTextColor ?? "#2F2B3DC7"}
      customFontSize={props?.customFontSize ?? "0.8125rem"}
      className={className}
      isAnimating={isAnimating}
      onFocus={handleFocus}
      onBlur={handleBlur}
      size={size}
      inputRef={ref}
      {...rest}
      variant="filled"
      InputLabelProps={{ ...InputLabelProps, shrink: true }}
    />
  );
});

export default CustomTextField;
